import {
  Children,
  JobType,
  LatLng,
  Location,
  PropertyListItem,
  PropertyGroup,
  FileLink,
  Vehicle,
  Violation,
  PhoneType,
  PropertyViolation,
  Property,
} from 'types';
import {
  ReactNode,
  createContext,
  useReducer,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import { getCountryByState } from 'utils';
import { AppContext } from './appContext';

const INITIAL_DATE = new Date();

interface JobBuilderState {
  // general
  jobType: JobType | null;
  property: Property | null;
  propertyGroup: PropertyGroup | null;
  contactPhone: string;
  contactPhoneType: PhoneType;
  phoneIsValid: boolean;
  requestTime: Date | null;
  // location
  locationID: number;
  street: string | null;
  unit: string | null;
  landmark: string | null;
  city: string | null;
  stateCode: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number;
  longitude: number;
  // vehicle
  vehicleID: number;
  vin: string | null;
  year: string | number | null;
  make: string | null;
  model: string | null;
  color: string | null;
  trim: string | null;
  plateNumber: string | null;
  plateState: string | null;
  plateExp: Date | null;
  // violations
  violations: Array<Violation>;
  // notes and attachments
  notes: string;
  fileKeys: Array<string>;
  signature: FileLink | null;
  // analytics
  changedDefaultPhone: boolean;
  changedRequestTime: boolean;
  changedDefaultAddress: boolean;
  // dropdown options
  violationListOptions: Array<PropertyViolation>;
  propertyGroupListOptions: Array<PropertyGroup>;
  propertyListOptions: Array<PropertyListItem>;
}

const defaultState: JobBuilderState = {
  // general
  jobType: JobType.TOW,
  property: null,
  propertyGroup: null,
  contactPhone: '',
  contactPhoneType: PhoneType.None,
  phoneIsValid: false,
  requestTime: INITIAL_DATE,
  // location
  locationID: 0,
  street: null,
  unit: null,
  landmark: null,
  city: null,
  stateCode: null,
  postalCode: null,
  country: null,
  latitude: 0,
  longitude: 0,
  // vehicle
  vehicleID: 0,
  vin: null,
  year: null,
  make: null,
  model: null,
  color: null,
  trim: null,
  plateNumber: null,
  plateState: null,
  plateExp: null,
  // violations
  violations: [],
  // notes
  notes: '',
  fileKeys: [],
  signature: null,
  // analytics
  changedDefaultPhone: false,
  changedRequestTime: false,
  changedDefaultAddress: false,
  // dropdown options
  violationListOptions: [],
  propertyGroupListOptions: [],
  propertyListOptions: [],
};

function getInitialState(): JobBuilderState {
  return { ...defaultState };
}
interface SET_JOB_TYPE {
  type: ACTION_TYPE.SET_JOB_TYPE;
  payload: JobType;
}
interface SET_PROPERTY {
  type: ACTION_TYPE.SET_PROPERTY;
  payload: Property | null;
}
interface SET_PROPERTY_GROUP {
  type: ACTION_TYPE.SET_PROPERTY_GROUP;
  payload: PropertyGroup | null;
}
interface SET_CONTACT_PHONE {
  type: ACTION_TYPE.SET_CONTACT_PHONE;
  payload: string;
}
interface SET_CONTACT_PHONE_TYPE {
  type: ACTION_TYPE.SET_CONTACT_PHONE_TYPE;
  payload: PhoneType;
}
interface SET_REQUEST_TIME {
  type: ACTION_TYPE.SET_REQUEST_TIME;
  payload: Date | null;
}
interface SET_LOCATION_ID {
  type: ACTION_TYPE.SET_LOCATION_ID;
  payload: number;
}
interface SET_STREET {
  type: ACTION_TYPE.SET_STREET;
  payload: string;
}
interface SET_UNIT {
  type: ACTION_TYPE.SET_UNIT;
  payload: string;
}
interface SET_LANDMARK {
  type: ACTION_TYPE.SET_LANDMARK;
  payload: string;
}
interface SET_CITY {
  type: ACTION_TYPE.SET_CITY;
  payload: string;
}
interface SET_STATE_CODE {
  type: ACTION_TYPE.SET_STATE_CODE;
  payload: string;
}
interface SET_POSTAL_CODE {
  type: ACTION_TYPE.SET_POSTAL_CODE;
  payload: string;
}
interface SET_LATLNG {
  type: ACTION_TYPE.SET_LATLNG;
  payload: LatLng;
}
interface SET_LOCATION {
  // for use when creating location on server
  type: ACTION_TYPE.SET_LOCATION;
  payload: Location;
}
interface AUTOCOMPLETE_ADDRESS {
  // for use with address autocomplete
  type: ACTION_TYPE.AUTOCOMPLETE_ADDRESS;
  payload: Location;
}
interface SET_VEHICLE {
  type: ACTION_TYPE.SET_VEHICLE;
  payload: Vehicle;
}
interface SET_VEHICLE_ID {
  type: ACTION_TYPE.SET_VEHICLE_ID;
  payload: number;
}
interface SET_VIN {
  type: ACTION_TYPE.SET_VIN;
  payload: string | null;
}
interface SET_YEAR {
  type: ACTION_TYPE.SET_YEAR;
  payload: string | null;
}
interface SET_MAKE {
  type: ACTION_TYPE.SET_MAKE;
  payload: string | null;
}
interface SET_MODEL {
  type: ACTION_TYPE.SET_MODEL;
  payload: string | null;
}
interface SET_COLOR {
  type: ACTION_TYPE.SET_COLOR;
  payload: string | null;
}
interface SET_TRIM {
  type: ACTION_TYPE.SET_TRIM;
  payload: string | null;
}
interface SET_PLATE_NUMBER {
  type: ACTION_TYPE.SET_PLATE_NUMBER;
  payload: string | null;
}
interface SET_PLATE_STATE {
  type: ACTION_TYPE.SET_PLATE_STATE;
  payload: string | null;
}
interface SET_PLATE_EXP {
  type: ACTION_TYPE.SET_PLATE_EXP;
  payload: Date | null;
}
interface SET_VIOLATIONS {
  type: ACTION_TYPE.SET_VIOLATIONS;
  payload: Array<Violation>;
}
interface SET_NOTES {
  type: ACTION_TYPE.SET_NOTES;
  payload: string;
}
interface SET_PHONE_IS_VALID {
  type: ACTION_TYPE.SET_PHONE_IS_VALID;
  payload: boolean;
}
interface ADD_FILE_KEYS {
  type: ACTION_TYPE.ADD_FILE_KEYS;
  payload: Array<string>;
}
interface REMOVE_FILE_KEYS {
  type: ACTION_TYPE.REMOVE_FILE_KEYS;
  payload: Array<string>;
}
interface SET_CHANGED_DEFAULT_PHONE {
  type: ACTION_TYPE.SET_CHANGED_DEFAULT_PHONE;
  payload: boolean;
}
interface SET_CHANGED_REQUEST_TIME {
  type: ACTION_TYPE.SET_CHANGED_REQUEST_TIME;
  payload: boolean;
}
interface SET_CHANGED_DEFAULT_ADDRESS {
  type: ACTION_TYPE.SET_CHANGED_DEFAULT_ADDRESS;
  payload: boolean;
}
interface SET_SIGNATURE {
  type: ACTION_TYPE.SET_SIGNATURE;
  payload: FileLink | null;
}

interface SET_VIOLATION_LIST_OPTIONS {
  type: ACTION_TYPE.SET_VIOLATION_LIST_OPTIONS;
  payload: Array<PropertyViolation>;
}
interface SET_PROPERTY_GROUPS {
  type: ACTION_TYPE.SET_PROPERTY_GROUPS;
  payload: Array<PropertyGroup>;
}
interface SET_PROPERTY_LIST_OPTIONS {
  type: ACTION_TYPE.SET_PROPERTY_LIST_OPTIONS;
  payload: Array<PropertyListItem>;
}

export const enum ACTION_TYPE {
  SET_JOB_TYPE = 'SET_JOB_TYPE',
  SET_PROPERTY = 'SET_PROPERTY',
  SET_PROPERTY_GROUP = 'SET_PROPERTY_GROUP',
  SET_CONTACT_PHONE = 'SET_CONTACT_PHONE',
  SET_CONTACT_PHONE_TYPE = 'SET_CONTACT_PHONE_TYPE',
  SET_PHONE_IS_VALID = 'SET_PHONE_IS_VALID',
  SET_REQUEST_TIME = 'SET_REQUEST_TIME',
  SET_LOCATION_ID = 'SET_LOCATION_ID',
  SET_STREET = 'SET_STREET',
  SET_UNIT = 'SET_UNIT',
  SET_LANDMARK = 'SET_LANDMARK',
  SET_CITY = 'SET_CITY',
  SET_STATE_CODE = 'SET_STATE_CODE',
  SET_POSTAL_CODE = 'SET_POSTAL_CODE',
  SET_LATLNG = 'SET_LATLNG',
  SET_LOCATION = 'SET_LOCATION',
  AUTOCOMPLETE_ADDRESS = 'AUTOCOMPLETE_ADDRESS',
  SET_VEHICLE = 'SET_VEHICLE',
  SET_VEHICLE_ID = 'SET_VEHICLE_ID',
  SET_VIN = 'SET_VIN',
  SET_YEAR = 'SET_YEAR',
  SET_MAKE = 'SET_MAKE',
  SET_MODEL = 'SET_MODEL',
  SET_COLOR = 'SET_COLOR',
  SET_TRIM = 'SET_TRIM',
  SET_PLATE_NUMBER = 'SET_PLATE_NUMBER',
  SET_PLATE_STATE = 'SET_PLATE_STATE',
  SET_PLATE_EXP = 'SET_PLATE_EXP',
  SET_VIOLATIONS = 'SET_VIOLATIONS',
  SET_NOTES = 'SET_NOTES',
  ADD_FILE_KEYS = 'ADD_FILE_KEYS',
  REMOVE_FILE_KEYS = 'REMOVE_FILE_KEYS',
  SET_CHANGED_DEFAULT_PHONE = 'SET_CHANGED_DEFAULT_PHONE',
  SET_CHANGED_REQUEST_TIME = 'SET_CHANGED_REQUEST_TIME',
  SET_CHANGED_DEFAULT_ADDRESS = 'SET_CHANGED_DEFAULT_ADDRESS',
  SET_SIGNATURE = 'SET_SIGNATURE',
  SET_VIOLATION_LIST_OPTIONS = 'SET_VIOLATION_LIST_OPTIONS',
  SET_PROPERTY_GROUPS = 'SET_PROPERTY_GROUPS',
  SET_PROPERTY_LIST_OPTIONS = 'SET_PROPERTY_LIST_OPTIONS',
}

type JobAction =
  | SET_JOB_TYPE
  | SET_PROPERTY
  | SET_PROPERTY_GROUP
  | SET_CONTACT_PHONE
  | SET_CONTACT_PHONE_TYPE
  | SET_PHONE_IS_VALID
  | SET_REQUEST_TIME
  | SET_LOCATION_ID
  | SET_STREET
  | SET_UNIT
  | SET_LANDMARK
  | SET_CITY
  | SET_STATE_CODE
  | SET_POSTAL_CODE
  | SET_LATLNG
  | SET_LOCATION
  | AUTOCOMPLETE_ADDRESS
  | SET_VEHICLE
  | SET_VEHICLE_ID
  | SET_VIN
  | SET_YEAR
  | SET_MAKE
  | SET_MODEL
  | SET_COLOR
  | SET_TRIM
  | SET_PLATE_NUMBER
  | SET_PLATE_STATE
  | SET_PLATE_EXP
  | SET_VIOLATIONS
  | SET_NOTES
  | ADD_FILE_KEYS
  | REMOVE_FILE_KEYS
  | SET_CHANGED_DEFAULT_PHONE
  | SET_CHANGED_REQUEST_TIME
  | SET_CHANGED_DEFAULT_ADDRESS
  | SET_SIGNATURE
  | SET_VIOLATION_LIST_OPTIONS
  | SET_PROPERTY_GROUPS
  | SET_PROPERTY_LIST_OPTIONS;

function reducer(state: JobBuilderState, action: JobAction) {
  let newState: JobBuilderState;
  switch (action.type) {
    case ACTION_TYPE.SET_JOB_TYPE:
      newState = {
        ...state,
        jobType: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PROPERTY:
      newState = {
        ...state,
        property: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PROPERTY_GROUP:
      newState = {
        ...state,
        propertyGroup: action.payload,
      };
      break;
    case ACTION_TYPE.SET_CONTACT_PHONE:
      newState = {
        ...state,
        contactPhone: action.payload,
      };
      break;
    case ACTION_TYPE.SET_CONTACT_PHONE_TYPE:
      newState = {
        ...state,
        contactPhoneType: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PHONE_IS_VALID:
      newState = {
        ...state,
        phoneIsValid: action.payload,
      };
      break;
    case ACTION_TYPE.SET_REQUEST_TIME:
      newState = {
        ...state,
        requestTime: action.payload,
      };
      break;
    case ACTION_TYPE.SET_LOCATION_ID:
      newState = {
        ...state,
        locationID: action.payload,
      };
      break;
    case ACTION_TYPE.SET_STREET:
      newState = {
        ...state,
        street: action.payload,
      };
      break;
    case ACTION_TYPE.SET_UNIT:
      newState = {
        ...state,
        unit: action.payload,
      };
      break;
    case ACTION_TYPE.SET_LANDMARK:
      newState = {
        ...state,
        landmark: action.payload,
      };
      break;
    case ACTION_TYPE.SET_CITY:
      newState = {
        ...state,
        city: action.payload,
      };
      break;
    case ACTION_TYPE.SET_STATE_CODE: // also sets country code automatically
      newState = {
        ...state,
        stateCode: action.payload,
        country: getCountryByState(action.payload),
      };
      break;
    case ACTION_TYPE.SET_POSTAL_CODE:
      newState = {
        ...state,
        postalCode: action.payload,
      };
      break;
    case ACTION_TYPE.SET_LATLNG:
      newState = {
        ...state,
        latitude: action.payload.lat,
        longitude: action.payload.lng,
      };
      break;
    case ACTION_TYPE.SET_LOCATION:
      newState = {
        ...state,
        locationID: action.payload.id || 0,
        street: action.payload.street || null,
        unit: action.payload.unit || null,
        landmark: action.payload.landmark || null,
        city: action.payload.city || null,
        stateCode: action.payload.stateCode || null,
        postalCode: action.payload.postalCode || null,
        country: action.payload.country || null,
        latitude: action.payload.latLng?.lat || 0,
        longitude: action.payload.latLng?.lng || 0,
      };
      break;
    case ACTION_TYPE.AUTOCOMPLETE_ADDRESS:
      newState = {
        ...state,
        street: action.payload.street || null,
        city: action.payload.city || null,
        stateCode: action.payload.stateCode || null,
        postalCode: action.payload.postalCode || null,
        country: action.payload.country || null,
      };
      break;
    case ACTION_TYPE.SET_VEHICLE: // for vehicle lookup use only
      newState = {
        ...state,
        vehicleID: action.payload.id || state.vehicleID,
        vin: action.payload.vin || state.vin,
        year: action.payload.year || state.year,
        make: action.payload.make || state.make,
        model: action.payload.model || state.model,
        trim: action.payload.trim || state.trim,
        color: action.payload.color || state.color,
        plateNumber: action.payload.plate?.number || state.plateNumber,
        plateState: action.payload.plate?.state || state.plateState,
      };
      break;
    case ACTION_TYPE.SET_VEHICLE_ID:
      newState = {
        ...state,
        vehicleID: action.payload,
      };
      break;
    case ACTION_TYPE.SET_VIN:
      newState = {
        ...state,
        vin: action.payload,
      };
      break;
    case ACTION_TYPE.SET_YEAR:
      newState = {
        ...state,
        year: action.payload,
      };
      break;
    case ACTION_TYPE.SET_MAKE:
      newState = {
        ...state,
        make: action.payload,
      };
      break;
    case ACTION_TYPE.SET_MODEL:
      newState = {
        ...state,
        model: action.payload,
      };
      break;
    case ACTION_TYPE.SET_COLOR:
      newState = {
        ...state,
        color: action.payload,
      };
      break;
    case ACTION_TYPE.SET_TRIM:
      newState = {
        ...state,
        trim: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PLATE_NUMBER:
      newState = {
        ...state,
        plateNumber: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PLATE_STATE:
      newState = {
        ...state,
        plateState: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PLATE_EXP:
      newState = {
        ...state,
        plateExp: action.payload,
      };
      break;
    case ACTION_TYPE.SET_VIOLATIONS:
      newState = {
        ...state,
        violations: action.payload,
      };
      break;
    case ACTION_TYPE.SET_NOTES:
      newState = {
        ...state,
        notes: action.payload,
      };
      break;
    case ACTION_TYPE.ADD_FILE_KEYS:
      newState = {
        ...state,
        fileKeys: [...state.fileKeys, ...action.payload],
      };
      break;
    case ACTION_TYPE.REMOVE_FILE_KEYS:
      newState = {
        ...state,
        fileKeys: state.fileKeys.filter((key) => !action.payload.includes(key)),
      };
      break;
    case ACTION_TYPE.SET_CHANGED_DEFAULT_PHONE:
      newState = {
        ...state,
        changedDefaultPhone: action.payload,
      };
      break;
    case ACTION_TYPE.SET_CHANGED_REQUEST_TIME:
      newState = {
        ...state,
        changedRequestTime: action.payload,
      };
      break;
    case ACTION_TYPE.SET_CHANGED_DEFAULT_ADDRESS:
      newState = {
        ...state,
        changedDefaultAddress: action.payload,
      };
      break;
    case ACTION_TYPE.SET_SIGNATURE:
      newState = {
        ...state,
        signature: action.payload,
      };
      break;
    case ACTION_TYPE.SET_VIOLATION_LIST_OPTIONS:
      newState = {
        ...state,
        violationListOptions: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PROPERTY_GROUPS:
      newState = {
        ...state,
        propertyGroupListOptions: action.payload,
      };
      break;
    case ACTION_TYPE.SET_PROPERTY_LIST_OPTIONS:
      newState = {
        ...state,
        propertyListOptions: action.payload,
      };
      break;
    default:
      newState = state;
  }

  return newState;
}

export const JobBuilderContext = createContext({
  state: getInitialState(),
  dispatch: (action: JobAction) => {
    // do nothing - this is only for the purpose of the initial value
  },
});

const JobBuilderContextProvider = ({ children }: Children): ReactNode => {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const { state: appState } = useContext(AppContext);

  useEffect(() => {
    const defaultPhone = appState.user?.contact?.primaryPhone;
    if (defaultPhone) {
      dispatch({ type: ACTION_TYPE.SET_CONTACT_PHONE, payload: defaultPhone });
      dispatch({
        type: ACTION_TYPE.SET_CONTACT_PHONE_TYPE,
        payload: appState.user?.contact?.primaryPhoneType || PhoneType.None,
      });
    }
  }, [appState.user]);


  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);
  return (
    <JobBuilderContext.Provider value={contextValue}>
      {children}
    </JobBuilderContext.Provider>
  );
};

export default JobBuilderContextProvider;
